// Port of SuiteModel/GridViews.swift (design-docs/Plot grid — design.md): a named set of outliner columns with its
// grouping and sort. Saved views are the project's and sync — one JSON entry per view at `settings["outlinerView.<id>"]`,
// flat keys in the settings map as comment categories are, so two devices' views merge. Built-in views aren't stored.
// Column ids are the Mac outliner's: `title synopsis status label pov words target compile position field-<field id>`
// (`storedColumn`/`webColumn` map the web outliner's own ids to them).
import { stableUUID } from '../hashing.ts';
import type { MetadataField } from '../model/documents.ts';
import { caseInsensitiveOrder } from '../story/glossary.ts';
import { fieldID, normalizedFieldName, visibleFields } from './fields.ts';

export interface GridViewSort { column: string; ascending: boolean }

export interface GridView {
  id: string;
  name: string;
  columns: string[];
  /** `none`, `status`, `label` or `pov`. */
  group: string;
  sort: GridViewSort | null;
}

export const GRID_VIEW_PREFIX = 'outlinerView.';
export const GRID_VIEW_NAME_LIMIT = 40;

/** `GridView.id(for:)`: a view saved under a name has the same id on every device. */
export const gridViewID = (name: string) => stableUUID('outliner-view:' + normalizedFieldName(name).toLowerCase());

/** `GridView.key`. */
export const gridViewKey = (name: string) => normalizedFieldName(name).normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

export const isBuiltInView = (view: Pick<GridView, 'id'>) => view.id.startsWith('builtin.');

/** `GridView(from:)`: throws unless id, name, columns and group read; a sort that doesn't read is none. */
export function decodeGridView(json: unknown): GridView {
  if (!json || typeof json !== 'object' || Array.isArray(json)) throw new Error('not an object');
  const o = json as Record<string, unknown>;
  if (typeof o.id !== 'string' || typeof o.name !== 'string' || typeof o.group !== 'string') throw new Error('view');
  if (!Array.isArray(o.columns) || !o.columns.every((c) => typeof c === 'string')) throw new Error('columns');
  const s = o.sort as Record<string, unknown> | null | undefined;
  const sort = s && typeof s.column === 'string' && typeof s.ascending === 'boolean' ? { column: s.column, ascending: s.ascending } : null;
  return { id: o.id, name: o.name, columns: o.columns as string[], group: o.group, sort };
}

/** The view as Swift's synthesised encoder writes it: no `sort` key when there's none. */
export const encodeGridView = (view: GridView): Record<string, unknown> =>
  view.sort ? { ...view } : { id: view.id, name: view.name, columns: view.columns, group: view.group };

/** `ProjectDocument.gridViews()`: the project's saved views, by name. */
export function readGridViews(settings: Record<string, unknown>): GridView[] {
  const out: GridView[] = [];
  for (const [key, json] of Object.entries(settings)) {
    if (!key.startsWith(GRID_VIEW_PREFIX) || typeof json !== 'string' || json === '') continue;
    try { out.push(decodeGridView(JSON.parse(json))); } catch { /* unreadable: left out */ }
  }
  return out.sort((a, b) => caseInsensitiveOrder(a.name, b.name));
}

/** `named`. */
export const gridViewNamed = (name: string, views: readonly GridView[]) => views.find((v) => gridViewKey(v.name) === gridViewKey(name));

/** Fields with a column of their own (or none), so not among the `field-` columns. */
const OWN_COLUMNS = new Set(['status', 'label', 'pov', 'word-target']);

/** `GridView.postsViewID`: a journal's Posts view, the one its outliner starts with. */
export const POSTS_VIEW_ID = 'builtin.posts';

/** `GridView.builtIn(fields:showsScenes:journal:)`: the views every project has, with the columns it has. A journal's posts
 *  (`journal`) have Posts (number, status, publish date, words, tags; grouped by status) and All columns. */
export function builtInGridViews(fields: readonly Pick<MetadataField, 'id' | 'type' | 'hidden'>[], showsScenes: boolean, journal = false): GridView[] {
  const shown = visibleFields(fields);
  const has = new Set(shown.map((f) => f.id));
  const fieldColumns = (ids: string[]) => ids.filter((id) => has.has(id)).map((id) => `field-${id}`);
  const story = showsScenes && !journal;
  const all = ['synopsis', 'status', 'label', ...(story ? ['pov'] : []), 'words', 'target', 'compile', ...(story ? ['position'] : []),
    ...shown.filter((f) => !OWN_COLUMNS.has(f.id) && f.type !== 'story-time').map((f) => `field-${f.id}`)];
  const view = (id: string, name: string, columns: string[], group = 'none'): GridView => ({ id, name, columns, group, sort: null });
  if (journal) {
    const posts = [...fieldColumns(['post-number']), 'status', ...fieldColumns(['publish-date']), 'words', ...fieldColumns(['tags'])];
    return [view(POSTS_VIEW_ID, 'Posts', posts, 'status'), view('builtin.all', 'All columns', all)];
  }
  const views = [view('builtin.all', 'All columns', all)];
  if (showsScenes) views.push(view('builtin.structure', 'Structure', [...fieldColumns(['beat']), 'position', 'pov', 'words']));
  views.push(view('builtin.progress', 'Progress', ['status', 'words', 'target', ...fieldColumns([fieldID('Done')])]));
  if (showsScenes) views.push(view('builtin.story', 'Story', ['pov', ...fieldColumns(['setting', 'story-duration'])]));
  return views;
}

// MARK: The web outliner's column ids

/** A web outliner column (`synopsis`, `words`, `position`, `field:<id>`) as a view stores it. */
export function storedColumn(web: string): string {
  if (!web.startsWith('field:')) return web;
  const id = web.slice('field:'.length);
  if (id === 'word-target') return 'target';
  return OWN_COLUMNS.has(id) ? id : `field-${id}`;
}

/** A stored column as the web outliner names it (`compile` has no column here; null). */
export function webColumn(stored: string): string | null {
  if (stored === 'compile') return null;
  if (stored === 'target') return 'field:word-target';
  if (stored === 'status' || stored === 'label' || stored === 'pov') return `field:${stored}`;
  if (stored.startsWith('field-')) return `field:${stored.slice('field-'.length)}`;
  return stored;
}
